import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ClipboardList, Plus, Camera, ChevronDown, ChevronUp, Trash2, Edit2, User, X, Check, Clock, FileText, DollarSign, ImageIcon, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MissionsPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [missions, setMissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [expandedMission, setExpandedMission] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [completeDialog, setCompleteDialog] = useState(null);
  const [completePhotos, setCompletePhotos] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [invoicePhotos, setInvoicePhotos] = useState([]);
  const photoRef = useRef(null);
  const docRef = useRef(null);
  const invoiceRef = useRef(null);

  const txt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);
  const isManager = user?.role === 'manager';

  const [form, setForm] = useState({
    title: '', description: '', project_id: '', client_name: '',
    amount: '', start_date: '', end_date: '', assigned_to: '', documents: [],
    invoices: [], photos_completed: [], estimated_days: ''
  });

  useEffect(() => { fetchMissions(); fetchEmployees(); fetchProjects(); }, []);

  const fetchMissions = async () => {
    try {
      const res = await axios.get(`${API}/missions`);
      setMissions(res.data || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setEmployees((res.data || []).filter(u => u.role === 'subcontractor'));
    } catch {}
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects/light`);
      setProjects((res.data || []).filter(p => !p.is_stock));
    } catch {}
  };

  const handleSave = async () => {
    if (!form.title) {
      toast.error(txt('Titre obligatoire', 'Заголовок обязателен', 'Titlu obligatoriu', 'Заголовок обов\'язковий'));
      return;
    }
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0, estimated_days: parseInt(form.estimated_days) || 0 };
      // Subcontractor creates missions assigned to themselves
      if (!isManager) {
        payload.assigned_to = user?.id;
      }
      await axios.post(`${API}/missions`, payload);
      toast.success(txt('Mission créée', 'Миссия создана', 'Misiune creată', 'Місію створено'));
      setShowDialog(false);
      resetForm();
      fetchMissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleComplete = async () => {
    if (!completeDialog) return;
    try {
      await axios.post(`${API}/missions/${completeDialog.id}/complete`, { photos_completed: completePhotos });
      toast.success(txt('Mission terminée', 'Миссия завершена', 'Misiune terminată', 'Місію завершено'));
      setCompleteDialog(null);
      setCompletePhotos([]);
      fetchMissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`${API}/missions/${id}/approve`);
      toast.success(txt('Mission approuvée', 'Миссия одобрена', 'Misiune aprobată', 'Місію схвалено'));
      fetchMissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => { setConfirmDelete(id); };

  const handleSaveInvoices = async () => {
    if (!invoiceDialog) return;
    try {
      await axios.put(`${API}/missions/${invoiceDialog.id}`, { invoices: invoicePhotos });
      toast.success(txt('Factures enregistrées', 'Счета сохранены', 'Facturi salvate', 'Рахунки збережено'));
      setInvoiceDialog(null);
      fetchMissions();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleInvoiceUpload = (e) => {
    Array.from(e.target.files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scale = Math.min(1, 1200 / img.width);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          setInvoicePhotos(prev => [...prev, canvas.toDataURL('image/jpeg', 0.6)]);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  const confirmDeleteMission = async () => {
    try {
      await axios.delete(`${API}/missions/${confirmDelete}`);
      toast.success(txt('Supprimée', 'Удалено', 'Șters', 'Видалено'));
      setConfirmDelete(null);
      fetchMissions();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const resetForm = () => {
    setForm({ title: '', description: '', project_id: '', client_name: '', amount: '', start_date: '', end_date: '', assigned_to: user?.role === 'subcontractor' ? user?.id : '', documents: [], invoices: [], photos_completed: [], estimated_days: '' });
  };

  const handleDocUpload = (e) => {
    Array.from(e.target.files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scale = Math.min(1, 1200 / img.width);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          setForm(prev => ({ ...prev, documents: [...prev.documents, canvas.toDataURL('image/jpeg', 0.6)] }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handlePhotoUpload = (e) => {
    Array.from(e.target.files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scale = Math.min(1, 1200 / img.width);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          setCompletePhotos(prev => [...prev, canvas.toDataURL('image/jpeg', 0.6)]);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const getStatusBadge = (status) => {
    const map = {
      assigned: { label: txt('En cours', 'В работе', 'În curs', 'В роботі'), color: 'bg-blue-100 text-blue-700' },
      completed: { label: txt('Terminée', 'Завершена', 'Terminată', 'Завершена'), color: 'bg-amber-100 text-amber-700' },
      approved: { label: txt('Approuvée', 'Одобрена', 'Aprobată', 'Схвалена'), color: 'bg-green-100 text-green-700' },
      paid: { label: txt('Payée', 'Оплачена', 'Plătită', 'Оплачена'), color: 'bg-slate-100 text-slate-500' },
    };
    return map[status] || map.assigned;
  };

  const [showArchived, setShowArchived] = useState(false);
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  
  const filteredByMonth = missions.filter(m => {
    const date = m.start_date || m.created_at?.slice(0, 10) || '';
    return date.startsWith(filterMonth);
  });
  const activeMissions = filteredByMonth.filter(m => m.status !== 'paid');
  const archivedMissions = filteredByMonth.filter(m => m.status === 'paid');
  const monthTotal = filteredByMonth.reduce((sum, m) => sum + (m.amount || 0), 0);
  const monthPaid = archivedMissions.reduce((sum, m) => sum + (m.amount || 0), 0);
  const monthPending = activeMissions.reduce((sum, m) => sum + (m.amount || 0), 0);

  const handlePay = async (id) => {
    try {
      await axios.post(`${API}/missions/${id}/pay`);
      toast.success(txt('Mission payée et classée', 'Миссия оплачена', 'Misiune plătită', 'Місію оплачено'));
      fetchMissions();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const locale = language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : language === 'uk' ? 'uk-UA' : 'fr-FR';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn pb-24" data-testid="missions-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--color-primary)' }}>
          <ClipboardList className="w-6 h-6" />
          {txt('Missions', 'Миссии', 'Misiuni', 'Місії')}
        </h1>
        {(isManager || !isManager) && (
          <Button onClick={() => { resetForm(); setShowDialog(true); }} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> {txt('Nouvelle', 'Новая', 'Nouă', 'Нова')}
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <div className="card-base p-3 text-center">
          <span className="text-2xl font-bold text-blue-600">{missions.filter(m => m.status === 'assigned').length}</span>
          <p className="text-[10px] text-gray-500">{txt('En cours', 'В работе', 'În curs', 'В роботі')}</p>
        </div>
        <div className="card-base p-3 text-center">
          <span className="text-2xl font-bold text-amber-600">{missions.filter(m => m.status === 'completed').length}</span>
          <p className="text-[10px] text-gray-500">{txt('À valider', 'Ожидают', 'De validat', 'Очікують')}</p>
        </div>
        <div className="card-base p-3 text-center">
          <span className="text-2xl font-bold text-green-600">{missions.filter(m => m.status === 'approved').length}</span>
          <p className="text-[10px] text-gray-500">{txt('Approuvées', 'Одобрено', 'Aprobate', 'Схвалено')}</p>
        </div>
        <div className="card-base p-3 text-center">
          <span className="text-2xl font-bold text-slate-400">{archivedMissions.length}</span>
          <p className="text-[10px] text-gray-500">{txt('Payées', 'Оплачено', 'Plătite', 'Оплачено')}</p>
        </div>
      </div>

      {/* Month filter + totals */}
      <div className="card-base p-3 space-y-3">
        <div className="flex items-center gap-3">
          <input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="px-3 py-1.5 border rounded-lg text-sm" />
          <span className="text-sm text-gray-500">{filteredByMonth.length} {txt('mission(s)', 'миссий', 'misiuni', 'місій')}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-slate-50 rounded-lg p-2">
            <p className="text-lg font-bold text-slate-800">{monthTotal.toFixed(0)}€</p>
            <p className="text-[10px] text-gray-500">{txt('Total mois', 'Итого за месяц', 'Total lună', 'Всього за місяць')}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-2">
            <p className="text-lg font-bold text-amber-700">{monthPending.toFixed(0)}€</p>
            <p className="text-[10px] text-gray-500">{txt('En attente', 'Ожидается', 'În așteptare', 'Очікується')}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-2">
            <p className="text-lg font-bold text-green-700">{monthPaid.toFixed(0)}€</p>
            <p className="text-[10px] text-gray-500">{txt('Payé', 'Оплачено', 'Plătit', 'Оплачено')}</p>
          </div>
        </div>
      </div>

      {/* Missions list */}
      {activeMissions.length === 0 && archivedMissions.length === 0 ? (
        <div className="card-base p-8 text-center">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">{txt('Aucune mission', 'Нет миссий', 'Nicio misiune', 'Немає місій')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeMissions.map(mission => {
            const badge = getStatusBadge(mission.status);
            const isExpanded = expandedMission === mission.id;
            return (
              <div key={mission.id} className="card-base overflow-hidden">
                <div className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setExpandedMission(isExpanded ? null : mission.id)}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${mission.status === 'approved' ? 'bg-green-100' : mission.status === 'completed' ? 'bg-amber-100' : 'bg-blue-100'}`}>
                      {mission.status === 'approved' ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : mission.status === 'completed' ? <Clock className="w-5 h-5 text-amber-600" /> : <ClipboardList className="w-5 h-5 text-blue-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-slate-900 truncate">{mission.title}</h3>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badge.color}`}>{badge.label}</span>
                        <span className="text-xs text-gray-500">{mission.assigned_to_name}</span>
                        {mission.amount > 0 && <span className="text-xs font-bold text-emerald-600">{mission.amount}€</span>}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t bg-gray-50 p-3 sm:p-4 space-y-3">
                    {mission.description && <div><p className="text-xs font-semibold text-gray-600 mb-1">{txt('Description', 'Описание', 'Descriere', 'Опис')}</p><p className="text-sm text-gray-700 bg-white rounded-lg p-2 border whitespace-pre-wrap">{mission.description}</p></div>}
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {mission.client_name && <div className="bg-white rounded-lg p-2 border"><span className="text-gray-500">{txt('Client', 'Клиент', 'Client', 'Клієнт')}</span><p className="font-semibold">{mission.client_name}</p></div>}
                      <div className="bg-white rounded-lg p-2 border"><span className="text-gray-500">{txt('Montant', 'Сумма', 'Sumă', 'Сума')}</span><p className="font-bold text-emerald-600">{mission.amount}€</p></div>
                      {mission.start_date && <div className="bg-white rounded-lg p-2 border"><span className="text-gray-500">{txt('Début', 'Начало', 'Început', 'Початок')}</span><p className="font-semibold">{new Date(mission.start_date + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short' })}</p></div>}
                      {mission.end_date && <div className="bg-white rounded-lg p-2 border"><span className="text-gray-500">{txt('Fin prévue', 'Дедлайн', 'Termen', 'Дедлайн')}</span><p className="font-semibold">{new Date(mission.end_date + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short' })}</p></div>}
                    </div>

                    {/* Documents/croquis */}
                    {mission.documents?.length > 0 && (
                      <div><p className="text-xs font-semibold text-gray-600 mb-1">{txt('Documents / Croquis', 'Документы', 'Documente', 'Документи')}</p>
                        <div className="flex gap-2 overflow-x-auto">{mission.documents.map((d, i) => <img key={i} src={d} alt="" className="w-20 h-20 rounded-lg object-cover border cursor-pointer" onClick={() => setPreviewPhoto(d)} />)}</div>
                      </div>
                    )}

                    {/* Completed photos */}
                    {mission.photos_completed?.length > 0 && (
                      <div><p className="text-xs font-semibold text-green-600 mb-1">{txt('Photos du travail', 'Фото работы', 'Foto lucrare', 'Фото роботи')}</p>
                        <div className="flex gap-2 overflow-x-auto">{mission.photos_completed.map((p, i) => <img key={i} src={p} alt="" className="w-20 h-20 rounded-lg object-cover border cursor-pointer" onClick={() => setPreviewPhoto(p)} />)}</div>
                      </div>
                    )}

                    {/* Invoices */}
                    {mission.invoices?.length > 0 && (
                      <div><p className="text-xs font-semibold text-purple-600 mb-1">{txt('Factures', 'Счета', 'Facturi', 'Рахунки')}</p>
                        <div className="flex gap-2 overflow-x-auto">{mission.invoices.map((p, i) => <img key={i} src={p} alt="" className="w-20 h-20 rounded-lg object-cover border cursor-pointer" onClick={() => setPreviewPhoto(p)} />)}</div>
                      </div>
                    )}

                    {/* Estimated days */}
                    {mission.estimated_days > 0 && (
                      <div className="bg-white rounded-lg p-2 border text-xs">
                        <span className="text-gray-500">{txt('Délai estimé', 'Оценка срока', 'Termen estimat', 'Оцінка терміну')}</span>
                        <p className="font-bold">{mission.estimated_days} {txt('jours', 'дней', 'zile', 'днів')}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-1 flex-wrap" onClick={e => e.stopPropagation()}>
                      {!isManager && (mission.status === 'assigned' || mission.status === 'completed') && (
                        <Button size="sm" variant="outline" className="text-xs text-green-600" onClick={() => { setCompleteDialog(mission); setCompletePhotos(mission.photos_completed || []); }}>
                          <Camera className="w-3 h-3 mr-1" />{txt('Photos', 'Фото', 'Foto', 'Фото')}
                        </Button>
                      )}
                      {!isManager && (
                        <Button size="sm" variant="outline" className="text-xs text-purple-600" onClick={() => { setInvoiceDialog(mission); setInvoicePhotos(mission.invoices || []); }}>
                          <FileText className="w-3 h-3 mr-1" />{txt('Factures', 'Счета', 'Facturi', 'Рахунки')}
                        </Button>
                      )}
                      {!isManager && mission.status === 'assigned' && (
                        <Button size="sm" className="text-xs bg-amber-500 hover:bg-amber-600" onClick={() => { setCompleteDialog(mission); setCompletePhotos(mission.photos_completed || []); }}>
                          <Check className="w-3 h-3 mr-1" />{txt('Terminée', 'Завершить', 'Terminată', 'Завершити')}
                        </Button>
                      )}
                      {isManager && mission.status === 'completed' && (
                        <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700" onClick={() => handleApprove(mission.id)}>
                          <CheckCircle2 className="w-3 h-3 mr-1" />{txt('Approuver', 'Одобрить', 'Aprobă', 'Схвалити')}
                        </Button>
                      )}
                      {isManager && mission.status === 'approved' && (
                        <Button size="sm" className="text-xs bg-slate-600 hover:bg-slate-700" onClick={() => handlePay(mission.id)}>
                          <DollarSign className="w-3 h-3 mr-1" />{txt('Payée', 'Оплачена', 'Plătită', 'Оплачена')}
                        </Button>
                      )}
                      {(isManager || mission.assigned_to === user?.id) && (
                        <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={() => handleDelete(mission.id)}>
                          <Trash2 className="w-3 h-3 mr-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Archived toggle */}
      {archivedMissions.length > 0 && (
        <div>
          <button onClick={() => setShowArchived(!showArchived)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 w-full py-2">
            {showArchived ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {txt(`Missions payées (${archivedMissions.length})`, `Оплаченные (${archivedMissions.length})`, `Plătite (${archivedMissions.length})`, `Оплачені (${archivedMissions.length})`)}
          </button>
          {showArchived && (
            <div className="space-y-2 opacity-60">
              {archivedMissions.map(mission => (
                <div key={mission.id} className="card-base p-3 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-500 truncate">{mission.title}</p>
                    <p className="text-xs text-slate-400">{mission.assigned_to_name} — {mission.amount}€</p>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{txt('Payée', 'Оплачена', 'Plătită', 'Оплачена')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Mission Dialog (Manager) */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" onOpenAutoFocus={e => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-blue-600" />{txt('Nouvelle mission', 'Новая миссия', 'Misiune nouă', 'Нова місія')}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">{txt('Titre', 'Заголовок', 'Titlu', 'Заголовок')}</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder={txt('Ex: Pose liner piscine', 'Напр: Укладка', 'Ex: Montaj', 'Напр: Монтаж')} className="mt-1" />
            </div>
            {/* Sous-traitant (manager only) */}
            {isManager && (
              <div>
                <Label className="text-xs">{txt('Sous-traitant', 'Субподрядчик', 'Subcontractant', 'Субпідрядник')}</Label>
                <select value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})} className="w-full mt-1 px-2 py-2 border border-gray-200 rounded-lg text-xs">
                  <option value="">{txt('— Choisir —', '— Выбрать —', '— Alegeți —', '— Виберіть —')}</option>
                  {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{txt('Montant (€)', 'Сумма (€)', 'Sumă (€)', 'Сума (€)')}</Label>
                <Input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="500" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">{txt('Délai (jours)', 'Срок (дней)', 'Termen (zile)', 'Термін (днів)')}</Label>
                <Input type="number" min="0" value={form.estimated_days} onChange={e => setForm({...form, estimated_days: e.target.value})} placeholder="5" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">{txt('Chantier', 'Объект', 'Șantier', 'Об\'єкт')}</Label>
                <select value={form.project_id} onChange={e => { const p = projects.find(x => x.id === e.target.value); setForm({...form, project_id: e.target.value, client_name: p?.name || ''}); }} className="w-full mt-1 px-2 py-2 border border-gray-200 rounded-lg text-xs">
                  <option value="">{txt('— Aucun —', '— Нет —', '— Niciunul —', '— Немає —')}</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{txt('Début', 'Начало', 'Început', 'Початок')}</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-xs">{txt('Fin prévue', 'Дедлайн', 'Termen', 'Дедлайн')}</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="mt-1" /></div>
            </div>
            <div>
              <Label className="text-xs">{txt('Description', 'Описание', 'Descriere', 'Опис')}</Label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder={txt('Détail de la mission...', 'Детали...', 'Detalii...', 'Деталі...')} className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" rows={3} />
            </div>
            {/* Documents/Croquis */}
            <div>
              <Label className="text-xs">{txt('Documents / Croquis', 'Документы', 'Documente', 'Документи')}</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {form.documents.map((d, i) => (
                  <div key={i} className="relative">
                    <img src={d} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                    <button type="button" onClick={() => setForm(prev => ({...prev, documents: prev.documents.filter((_, idx) => idx !== i)}))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => docRef.current?.click()} className="flex-1 py-2 border-2 border-dashed border-blue-300 rounded-lg flex items-center justify-center gap-2 text-blue-500 hover:bg-blue-50 text-xs font-medium">
                  <FileText className="w-4 h-4" /> {txt('Ajouter document', 'Добавить документ', 'Adaugă document', 'Додати документ')}
                </button>
                <input ref={docRef} type="file" accept="image/*" multiple className="hidden" onChange={handleDocUpload} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setShowDialog(false)} variant="outline" className="flex-1">{txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}</Button>
              <Button onClick={handleSave} className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />{txt('Créer', 'Создать', 'Creează', 'Створити')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Mission Dialog (Subcontractor) */}
      <Dialog open={!!completeDialog} onOpenChange={() => setCompleteDialog(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" onOpenAutoFocus={e => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Check className="w-5 h-5 text-amber-600" />{txt('Terminer la mission', 'Завершить миссию', 'Termină misiunea', 'Завершити місію')}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-600"><strong>{completeDialog?.title}</strong></p>
            <div>
              <Label className="text-xs text-green-600 font-semibold">{txt('Photos du travail terminé', 'Фото выполненной работы', 'Foto lucrare terminată', 'Фото виконаної роботи')}</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {completePhotos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                    <button type="button" onClick={() => setCompletePhotos(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => photoRef.current?.click()} className="flex-1 py-2 border-2 border-dashed border-green-300 rounded-lg flex items-center justify-center gap-2 text-green-500 hover:bg-green-50 text-xs font-medium">
                  <Camera className="w-4 h-4" /> {txt('Caméra', 'Камера', 'Cameră', 'Камера')}
                </button>
                <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
                <input type="file" accept="image/*" multiple className="hidden" id="photo-lib-mission" onChange={handlePhotoUpload} />
                <button type="button" onClick={() => document.getElementById('photo-lib-mission')?.click()} className="flex-1 py-2 border-2 border-dashed border-green-300 rounded-lg flex items-center justify-center gap-2 text-green-500 hover:bg-green-50 text-xs font-medium">
                  <ImageIcon className="w-4 h-4" /> {txt('Galerie', 'Галерея', 'Galerie', 'Галерея')}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setCompleteDialog(null)} variant="outline" className="flex-1">{txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}</Button>
              <Button onClick={handleComplete} className="flex-1 bg-amber-500 hover:bg-amber-600">
                <Check className="w-4 h-4 mr-2" />{txt('Mission terminée', 'Завершить', 'Misiune terminată', 'Завершити')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete */}

      {/* Invoice Dialog */}
      <Dialog open={!!invoiceDialog} onOpenChange={() => setInvoiceDialog(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" onOpenAutoFocus={e => e.preventDefault()}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-purple-600" />{txt('Factures', 'Счета', 'Facturi', 'Рахунки')}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-600"><strong>{invoiceDialog?.title}</strong></p>
            <div className="flex gap-2 mt-1 flex-wrap">
              {invoicePhotos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                  <button type="button" onClick={() => setInvoicePhotos(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => invoiceRef.current?.click()} className="flex-1 py-2 border-2 border-dashed border-purple-300 rounded-lg flex items-center justify-center gap-2 text-purple-500 hover:bg-purple-50 text-xs font-medium">
                <Camera className="w-4 h-4" /> {txt('Ajouter facture', 'Добавить счёт', 'Adaugă factură', 'Додати рахунок')}
              </button>
              <input ref={invoiceRef} type="file" accept="image/*" multiple className="hidden" onChange={handleInvoiceUpload} />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setInvoiceDialog(null)} variant="outline" className="flex-1">{txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}</Button>
              <Button onClick={handleSaveInvoices} className="flex-1 bg-purple-600 hover:bg-purple-700">
                <FileText className="w-4 h-4 mr-2" />{txt('Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-sm" onOpenAutoFocus={e => e.preventDefault()}>
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2"><Trash2 className="w-5 h-5" />{txt('Supprimer cette mission ?', 'Удалить миссию?', 'Ștergeți misiunea?', 'Видалити місію?')}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 py-2">{txt('Cette action est irréversible.', 'Это действие необратимо.', 'Acțiune ireversibilă.', 'Ця дія незворотна.')}</p>
          <div className="flex gap-3">
            <Button onClick={() => setConfirmDelete(null)} variant="outline" className="flex-1">{txt('Annuler', 'Отмена', 'Anulare', 'Скасувати')}</Button>
            <Button onClick={confirmDeleteMission} className="flex-1 bg-red-600 hover:bg-red-700 text-white">{txt('Supprimer', 'Удалить', 'Șterge', 'Видалити')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Preview */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[99999] p-4" onClick={() => setPreviewPhoto(null)}>
          <button onClick={() => setPreviewPhoto(null)} className="absolute top-4 right-4 text-white"><X className="w-8 h-8" /></button>
          <img src={previewPhoto} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}
